import { useEffect, useState, type ReactNode } from 'react'
import { useEquipmentScope } from './equipmentScope'
import { Pencil } from 'lucide-react'
import { Dialog, DialogContent, DialogFooter } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { EquipmentDocumentsPanel } from './EquipmentDocumentsPanel'
import { RoomPicker } from './facility/RoomPicker'
import { FloorPicker } from './facility/FloorPicker'
import type { Equipment } from '@/types/equipment'

export type EditEquipmentInput = {
  label: string
  model?: string
  serial?: string
  vendor?: string
  installed?: number
  /** Rooms only (Cleaning Visual): null clears. */
  grade?: string | null
  areaSqm?: number | null
  /** The room it stands in (z8nrz7fw25); omitted when unchanged, null takes it out. */
  roomId?: string | null
  /** Rooms only: the floor it is on; omitted when unchanged, null takes it off. */
  floorId?: string | null
}

function Labeled({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className='space-y-1'>
      <label className='text-xs font-medium text-gray-600'>{label}</label>
      {children}
    </div>
  )
}

/** Edit a unit's own fields. Equipment Type and Group are identity + drive the
 * schedule, so they're shown read-only here (change them by re-creating the unit). */
export function EditEquipmentModal({
  equipment,
  onClose,
  onSave,
}: {
  equipment: Equipment | null
  onClose: () => void
  onSave: (id: string, input: EditEquipmentInput) => void
}) {
  const { copy, variant } = useEquipmentScope()
  const isRoom = variant === 'room'
  const [form, setForm] = useState({
    label: '',
    model: '',
    serial: '',
    vendor: '',
    installed: '',
    grade: '',
    areaSqm: '',
  })
  const [roomId, setRoomId] = useState<string | null>(null)
  const [floorId, setFloorId] = useState<string | null>(null)

  useEffect(() => {
    if (!equipment) return
    setRoomId(equipment.roomId ?? null)
    setFloorId(equipment.floorId ?? null)
    const blank = (v: string) => (v === '—' ? '' : v)
    setForm({
      label: equipment.equipmentId,
      model: blank(equipment.model),
      serial: blank(equipment.serial),
      vendor: blank(equipment.vendor),
      installed: equipment.installed ? String(equipment.installed) : '',
      grade: equipment.grade ?? '',
      areaSqm: equipment.areaSqm != null ? String(equipment.areaSqm) : '',
    })
  }, [equipment])

  const set = (key: keyof typeof form, value: string) =>
    setForm((prev) => ({ ...prev, [key]: value }))

  const areaValid = form.areaSqm.trim() === '' || (Number.isFinite(Number(form.areaSqm)) && Number(form.areaSqm) >= 0)
  const canSubmit = form.label.trim() !== '' && areaValid

  const handleSave = () => {
    if (!equipment || !canSubmit) return
    onSave(equipment.id, {
      label: form.label.trim(),
      // A room has no model, serial or vendor: none is sent, so none changes.
      ...(isRoom ? {} : { model: form.model.trim(), serial: form.serial.trim(), vendor: form.vendor.trim() }),
      installed: form.installed ? Number(form.installed) : undefined,
      ...(isRoom
        ? {
            grade: form.grade.trim() || null,
            areaSqm: form.areaSqm.trim() === '' ? null : Number(form.areaSqm),
            ...(floorId !== (equipment.floorId ?? null) ? { floorId } : {}),
          }
        : {}),
      // Only a change is sent: moving a unit is refused while a transfer runs on it.
      ...(!isRoom && roomId !== (equipment.roomId ?? null) ? { roomId } : {}),
    })
    onClose()
  }

  return (
    <Dialog open={equipment !== null} onOpenChange={(o) => !o && onClose()}>
      {/* Scrolls now that a document list of unbounded length sits above the
          footer — matching EditEquipmentTypeModal, which already did. */}
      <DialogContent className='max-h-[90vh] overflow-y-auto sm:max-w-lg'>
        {equipment && (
          <>
            <div className='flex items-start gap-3'>
              <div className='flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary'>
                <Pencil className='h-5 w-5' />
              </div>
              <div className='min-w-0'>
                <h2 className='text-base font-semibold text-gray-900'>{copy.editUnit}</h2>
                <p className='mt-0.5 text-xs text-muted-foreground'>
                  {equipment.equipmentType} · {equipment.group}
                </p>
              </div>
            </div>

            <div className='mt-4 grid grid-cols-2 gap-3'>
              <Labeled label={copy.unitId}>
                <Input value={form.label} onChange={(e) => set('label', e.target.value)} />
              </Labeled>
              <Labeled label='Installed (year)'>
                <Input
                  type='number'
                  value={form.installed}
                  onChange={(e) => set('installed', e.target.value)}
                  placeholder='e.g. 2024'
                />
              </Labeled>
              {/* A room has no model, serial or vendor (NYP feedback, 2026-10-29). */}
              {!isRoom && (
                <>
                  <Labeled label='Model'>
                    <Input value={form.model} onChange={(e) => set('model', e.target.value)} placeholder='Model' />
                  </Labeled>
                  <Labeled label='Serial'>
                    <Input value={form.serial} onChange={(e) => set('serial', e.target.value)} placeholder='SN-…' />
                  </Labeled>
                  <Labeled label='Vendor'>
                    <Input value={form.vendor} onChange={(e) => set('vendor', e.target.value)} placeholder='Vendor' />
                  </Labeled>
                </>
              )}
            </div>
            {/* Where it is: equipment by its room; a room by its floor, never another room. */}
            <div className='mt-3'>
              {isRoom ? (
                <Labeled label='Floor'>
                  <FloorPicker value={floorId} onChange={setFloorId} />
                </Labeled>
              ) : (
                <Labeled label='Room'>
                  <RoomPicker value={roomId} onChange={setRoomId} />
                </Labeled>
              )}
            </div>
            {isRoom && (
              <div className='mt-3 grid grid-cols-2 gap-3'>
                <Labeled label='Grade'>
                  <Input value={form.grade} onChange={(e) => set('grade', e.target.value)} placeholder='e.g. B, C/D, CNC' maxLength={16} />
                </Labeled>
                <Labeled label='Area (m²)'>
                  <Input
                    type='number'
                    min={0}
                    value={form.areaSqm}
                    onChange={(e) => set('areaSqm', e.target.value)}
                    placeholder='e.g. 42'
                    aria-invalid={!areaValid || undefined}
                  />
                </Labeled>
              </div>
            )}

            {/*
              Uploaded immediately, not on "Save changes" — an upload needs the
              unit's id. The unit's own documents are editable here; the ones it
              inherits from its type are listed read-only.
            */}
            <div className='mt-4'>
              <EquipmentDocumentsPanel owner={{ kind: 'unit', id: equipment.id }} />
            </div>

            <DialogFooter className='mt-5'>
              <Button variant='outline' onClick={onClose}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={!canSubmit}>
                Save changes
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
